import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import GeneralCard from './generalCard';
import { TaskDetailSections, taskFields, taskRelations } from '../../constants/tasks';
import { useLocation, useParams } from 'react-router-dom';
import { useFetchTaskDetail } from '../../hooks/useFetchTaskDetail';
import AddEditTransactionHeader from '../dashboard/addTransaction/addEditTransactionHeader';
import { useDeleteTask } from '../../hooks/useDeleteTask';
import { usePage } from '../../context/PageContext';
import ConfirmModal from '../confirmationmodal/confirmationModal';
import { Delete, LinearCloseCirlce } from '../icons';
import { ITask } from '../../interface/tasks';
import { useQueryClient } from '@tanstack/react-query';
import { useTask } from '../../context/TaskContext';
import { useNavigationStack } from '../../context/NavigationStackContext';
import Notes from '../conflictSearch/notes';
import { NotesEntity } from '../../constants/notes';
import WorkTimes from './workTimes';
import { useMarkActivityAsCompleted } from '../../hooks/useMarkActivityAsCompleted';
import { useOpenActivity } from '../../hooks/useOpenActivity';
import CustomModal from '../informationPopup/customModal';
import { useAnalytics } from '../../hooks/useAnalytics';
import { AnalyticsPage } from '../../interface/analytics';
import CustomTabs from '../customTabs/customTabs';
import { useEmailCategoryTagger } from '../../hooks/useEmailCategoryTagger';
import DetailScrollArea from '../detailScrollArea/detailScrollArea';

const TaskDetailView = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { trackPage } = useAnalytics();
  const fromTab = Object.values(TaskDetailSections).includes(location?.state?.fromTab)
    ? location.state.fromTab
    : TaskDetailSections.general;
  const { id: taskId } = useParams();
  const { updateTask } = useTask();
  const { goBack } = useNavigationStack();
  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  const [markAsCompleteConfirmationModalVisible, setMarkAsCompleteConfirmationModalVisible] =
    useState<boolean>(false);
  const [unableToDeleteTaskModalLinkedWorkTimes, setUnableToDeleteTaskModalLinkedWorkTimes] =
    useState<boolean>(false);

  const [selectedTab, setSelectedTab] = useState<string>(String(fromTab));
  const { setTitle } = usePage();
  const { untagEmailCategory } = useEmailCategoryTagger();
  const deleteTaskMutation = useDeleteTask();
  const markAsCompletedMutation = useMarkActivityAsCompleted();
  const openActivityMutation = useOpenActivity();
  const {
    data: task,
    isLoading,
    refetch,
  } = useFetchTaskDetail({
    id: taskId,
    relations: taskRelations,
    fields: taskFields,
  });

  useEffect(() => {
    trackPage(AnalyticsPage.TASK_DETAIL);
  }, [trackPage]);

  const markTaskAsCompleteOrOpen = (operation?: string) => {
    if (!task) return;
    const onSettled = {
      onSettled() {
        queryClient.removeQueries({ queryKey: ['task-details'] });
        refetch();
      },
    };
    if (operation === 'complete') {
      markAsCompletedMutation.mutate(
        {
          id: task?.id,
          version: task?.version,
        },
        onSettled,
      );
    } else {
      openActivityMutation.mutate(
        {
          id: task?.id,
          version: task?.version,
        },
        onSettled,
      );
    }
  };
  const handleMarkAsCompleteOrOpen = (task?: ITask, operation?: string) => {
    if (task) {
      if (operation === 'open') {
        markTaskAsCompleteOrOpen(operation);
        return;
      }
      const createTransactionWhenMarkAsDone = task.activityType.createTransactionWhenMarkedAsDone;
      const workTimeInvoicedThroughActivity = task.activityType.workTimeInvoicedThroughActivity;
      const workTimesDurationMoreThan0 = task.workTimes?.some((workTime) => workTime.duration > 0);
      if (
        !createTransactionWhenMarkAsDone &&
        workTimeInvoicedThroughActivity &&
        workTimesDurationMoreThan0 &&
        !task.transactionId
      ) {
        setMarkAsCompleteConfirmationModalVisible(true);
        return;
      }
      markTaskAsCompleteOrOpen('complete');
    }
  };

  const items = useMemo(
    () => [
      {
        key: String(TaskDetailSections.general),
        label: t('common:tab.general'),
        children: (
          <DetailScrollArea>
            <GeneralCard
              data-testid="task-detail-general-card"
              task={task}
              completeOpenTask={handleMarkAsCompleteOrOpen}
              isLoading={isLoading}
            />
          </DetailScrollArea>
        ),
      },
      {
        key: String(TaskDetailSections.worktimes),
        label: t('common:tab.worktimes'),
        children: <WorkTimes data-testid="task-detail-worktimes" task={task as ITask} />,
      },
      {
        key: String(TaskDetailSections.notes),
        label: t('common:tab.notes'),
        children: (
          <Notes
            data-testid="task-detail-notes"
            entitySubject={task?.subject ?? ''}
            entity={NotesEntity.ACTIVITY}
          />
        ),
      },
    ],
    [task, isLoading],
  );

  const handleConfirmDelete = () => {
    if (task?.workTimes?.length) {
      setUnableToDeleteTaskModalLinkedWorkTimes(true);
    } else {
      setShowDeleteConfirm(true);
    }
  };
  const handleBack = () => {
    updateTask(null);
    goBack();
  };

  const deleteTask = () => {
    deleteTaskMutation.mutate(taskId ?? '', {
      async onSuccess() {
        const categoryName = `[CSI - ${t('task:task')}]`;
        await untagEmailCategory(categoryName, { taskId: '' });
        handleBack();
      },
      onSettled() {
        setShowDeleteConfirm(false);
      },
    });
  };

  useEffect(() => {
    if (task) {
      setTitle(
        <AddEditTransactionHeader
          handleBack={handleBack}
          title={task?.subject}
          subTitle={t('task:tasks')}
          handleDelete={handleConfirmDelete}
        />,
      );
      updateTask(task);
    }
  }, [task]);

  return (
    <div className="dashboard-wrapper">
      <CustomTabs variant="pill" activeKey={selectedTab} onChange={setSelectedTab} items={items} />

      <ConfirmModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        icon={<Delete />}
        title={t('task:deleteTaskQuestion')}
        subtitle={t('task:deleteTaskText')}
        cancelText={t('common:cancel')}
        confirmText={t('note:delete')}
        onConfirm={deleteTask}
      />
      <CustomModal
        visible={markAsCompleteConfirmationModalVisible}
        title={t('task:markAsCompleteConfirmation')}
        cancelText={t('common:cancel')}
        confirmText={t('common:continue')}
        onConfirm={() => {
          markTaskAsCompleteOrOpen('complete');
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
        onClose={() => {
          setMarkAsCompleteConfirmationModalVisible(false);
        }}
      />
      <CustomModal
        visible={unableToDeleteTaskModalLinkedWorkTimes}
        icon={<LinearCloseCirlce />}
        title={t('task:unableToDeleteTaskLinkedWorkTimes')}
        subdescription={t('task:unableToDeleteTaskLinkedWorkTimesDescription')}
        confirmText={t('common:ok')}
        onConfirm={() => {
          setUnableToDeleteTaskModalLinkedWorkTimes(false);
        }}
      />
    </div>
  );
};

export default TaskDetailView;
